import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  useCommunity,
  useLeaveCommunity,
  useMyCommunity,
  useRequestToJoinCommunity,
} from "../hooks/useCommunity";
import LeaderboardList from "../components/LeaderboardList";
import LeaderboardTabs from "../components/LeaderboardTabs";
import CommunityType from "../interfaces/community";
import useUserStore from "../store/userStore";
import { ASSETS_URL } from "../util/constants";
import "./CommunityDetails.css";

const getCommunityLogo = (community: CommunityType) => {
  const logoPath = community.logo || community.pathImage;

  if (!logoPath) {
    return "/logo.png";
  }

  if (logoPath.startsWith("http")) {
    return logoPath;
  }

  return `${ASSETS_URL}${logoPath.startsWith("/") ? logoPath : `/${logoPath}`}`;
};

const CommunityDetails = () => {
  const { id } = useParams();
  const communityId = Number(id);
  const navigate = useNavigate();
  const token = useUserStore((state) => state.token);
  const username = useUserStore((state) => state.username);
  const setDestinyRoute = useUserStore((state) => state.setDestinyRoute);
  const [feedback, setFeedback] = useState("");
  const [activeTab, setActiveTab] = useState<"members" | "leaderboard">(
    "members",
  );

  const { data: community, isLoading, error } = useCommunity(communityId);
  const { data: myCommunity, isLoading: isLoadingMyCommunity } =
    useMyCommunity(token);
  const requestToJoin = useRequestToJoinCommunity();
  const leaveCommunity = useLeaveCommunity();

  const isOwner = community?.owner?.username === username;
  const isMember = useMemo(
    () => community?.members?.some((member) => member.username === username),
    [community?.members, username],
  );
  const hasPendingRequest = useMemo(
    () => community?.requests?.some((request) => request.username === username),
    [community?.requests, username],
  );
  const hasAnyCommunity = Boolean(myCommunity);
  const canRequestJoin =
    Boolean(token) &&
    !isLoadingMyCommunity &&
    !hasAnyCommunity &&
    !hasPendingRequest &&
    !isMember;

  const requireLogin = () => {
    setDestinyRoute(`/community/${communityId}`);
    navigate("/login");
  };

  const handleRequestToJoin = () => {
    if (!token || !community?.id) {
      requireLogin();
      return;
    }

    setFeedback("");
    requestToJoin.mutate(
      { communityId: community.id, token },
      {
        onSuccess: () => setFeedback("Your request was sent to the owner."),
        onError: () => setFeedback("Could not send the request."),
      },
    );
  };

  const handleLeave = () => {
    if (!token) {
      requireLogin();
      return;
    }

    setFeedback("");
    leaveCommunity.mutate(token, {
      onSuccess: () => {
        setFeedback("You left the community.");
        navigate("/community");
      },
      onError: () => setFeedback("Could not leave the community."),
    });
  };

  const handleDelete = () => {
    if (!token || !window.confirm("Delete this community?")) {
      return;
    }

    leaveCommunity.mutate(token, {
      onSuccess: () => navigate("/community"),
      onError: () => setFeedback("Could not delete the community."),
    });
  };

  if (Number.isNaN(communityId)) {
    return <div className="community-detail-page">Community not found.</div>;
  }

  if (isLoading) {
    return <div className="community-detail-page">Loading community...</div>;
  }

  if (error || !community) {
    return <div className="community-detail-page">Community not found.</div>;
  }

  return (
    <main className="community-detail-page">
      <section className="community-detail-hero">
        <img
          src={getCommunityLogo(community)}
          alt={community.name}
          className="community-detail-logo"
        />

        <div className="community-detail-copy">
          <Link className="community-detail-back" to="/community">
            Back to communities
          </Link>
          <h1>{community.name}</h1>
          <p>{community.description || "No description provided."}</p>
          <div className="community-detail-meta">
            <span>Owner: {community.owner?.username ?? "Unknown"}</span>
            <span>{community.members?.length ?? 0} members</span>
            <span>{community.requests?.length ?? 0} pending requests</span>
          </div>
        </div>

        <div className="community-detail-actions">
          {!token && (
            <button type="button" onClick={requireLogin}>
              Login to join
            </button>
          )}
          {canRequestJoin && (
            <button
              type="button"
              onClick={handleRequestToJoin}
              disabled={requestToJoin.isPending}
            >
              {requestToJoin.isPending ? "Sending..." : "Request to Join"}
            </button>
          )}
          {hasPendingRequest && (
            <button type="button" disabled>
              Request Pending
            </button>
          )}
          {isMember && !isOwner && (
            <button
              type="button"
              className="community-detail-secondary"
              onClick={handleLeave}
              disabled={leaveCommunity.isPending}
            >
              {leaveCommunity.isPending ? "Leaving..." : "Leave Community"}
            </button>
          )}
          {isOwner && (
            <>
              <button
                type="button"
                onClick={() => navigate(`/community/${community.id}/manage`)}
              >
                Manage Community
              </button>
              <button
                type="button"
                className="community-detail-danger"
                onClick={handleDelete}
                disabled={leaveCommunity.isPending}
              >
                {leaveCommunity.isPending ? "Deleting..." : "Delete Community"}
              </button>
            </>
          )}
          {token &&
            !isLoadingMyCommunity &&
            hasAnyCommunity &&
            !isMember &&
            !hasPendingRequest && (
            <p className="community-detail-note">
              You already belong to another community.
            </p>
          )}
        </div>
      </section>

      {feedback && <p className="community-detail-feedback">{feedback}</p>}

      <div className="community-detail-tabs" role="tablist">
        <button
          type="button"
          className={activeTab === "members" ? "active" : ""}
          onClick={() => setActiveTab("members")}
        >
          Members
        </button>
        <button
          type="button"
          className={activeTab === "leaderboard" ? "active" : ""}
          onClick={() => setActiveTab("leaderboard")}
        >
          Leaderboard
        </button>
      </div>

      {activeTab === "members" && (
        <section className="community-detail-section">
          <h2>Members</h2>
          {community.members?.length ? (
            <div className="community-detail-list">
              {community.members.map((member) => (
                <Link
                  className="community-detail-person"
                  key={member.id ?? member.username}
                  to={`/profile/${member.username}`}
                >
                  {member.username}
                </Link>
              ))}
            </div>
          ) : (
            <p className="community-detail-empty">No members yet.</p>
          )}
        </section>
      )}

      {activeTab === "leaderboard" && (
        <section className="community-detail-leaderboard">
          <LeaderboardTabs />
          <LeaderboardList
            communityId={communityId}
            title={`${community.name} Leaderboard`}
            emptyMessage="No community member found for this medal."
          />
        </section>
      )}
    </main>
  );
};

export default CommunityDetails;
